import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Calendar, Wrench, Droplets, Flame, ShowerHead, Thermometer, Construction, Home, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/common/CTASection';
import { BreadcrumbSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { services, businessInfo } from '../data/mock';
import { seoConfig } from '../data/seoConfig';

const iconMap = {
  Droplets: Droplets,
  Flame: Flame,
  Construction: Construction,
  ShowerHead: ShowerHead,
  Thermometer: Thermometer,
  Shovel: Wrench,
  Stethoscope: Shield,
  Home: Home
};

const ServicesPage = () => {
  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: 'Services', url: 'https://basinplumbing.com/services/' }
  ];

  return (
    <>
      <Seo {...seoConfig.services} />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#1a3a5c] to-[#0f2438] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"></div>
          
          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <nav className="text-sm text-blue-200 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">Services</span>
            </nav>
            
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Plumbing Services in Odessa & Midland
              </h1>
              <p className="text-xl text-blue-100 leading-relaxed mb-8">
                From emergency repairs to planned installations, we handle all your residential 
                and commercial plumbing needs. Licensed, insured, and available 24/7.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-[#e85a24] hover:bg-[#d14a14] text-white px-8">
                    <Calendar className="mr-2" size={20} />
                    Schedule Service
                  </Button>
                </Link>
                <a href={`tel:${businessInfo.phone}`}>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1a3a5c] px-8">
                    <Phone className="mr-2" size={20} />
                    {businessInfo.phone}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-[#1a3a5c] mb-4">Our Plumbing Services</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Click on any service to learn more, or book directly to schedule your appointment.
              </p>
            </div>

            <div className="space-y-8">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Wrench;
                return (
                  <Card key={service.id} className="border-0 shadow-lg hover:shadow-xl transition-all overflow-hidden">
                    <CardContent className="p-0">
                      <div className={`grid lg:grid-cols-2 ${index % 2 === 1 ? '' : ''}`}>
                        {/* Image Side */}
                        <div className={`relative h-64 lg:h-auto ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                          <img 
                            src={service.image} 
                            alt={`${service.title} service in Odessa Midland TX`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent lg:hidden"></div>
                        </div>
                        
                        {/* Content Side */}
                        <div className={`p-8 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-14 h-14 bg-[#1a3a5c]/10 rounded-xl flex items-center justify-center">
                              <IconComponent className="text-[#1a3a5c]" size={28} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#1a3a5c]">{service.title}</h3>
                          </div>
                          
                          <p className="text-gray-600 mb-6 leading-relaxed">
                            {service.description}
                          </p>
                          
                          {/* Features */}
                          <div className="grid sm:grid-cols-2 gap-2 mb-6">
                            {service.features.slice(0, 4).map((feature) => (
                              <div key={feature} className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-[#e85a24] flex-shrink-0" />
                                <span className="text-gray-700 text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-col sm:flex-row gap-3">
                            <Link to={`/services/${service.slug}`}>
                              <Button className="bg-[#1a3a5c] hover:bg-[#2a5a8c] text-white w-full sm:w-auto">
                                Learn More
                                <ArrowRight className="ml-2" size={18} />
                              </Button>
                            </Link>
                            <Link to="/contact">
                              <Button variant="outline" className="border-[#e85a24] text-[#e85a24] hover:bg-[#e85a24] hover:text-white w-full sm:w-auto">
                                <Calendar className="mr-2" size={18} />
                                Book This Service
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1a3a5c] mb-4">Why Choose Basin Plumbing?</h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: '24/7 Availability', desc: 'Emergency service around the clock. We answer when you call.' },
                { title: 'Upfront Pricing', desc: 'Know what you\'ll pay before we start. No surprise bills.' },
                { title: 'Licensed Experts', desc: 'All plumbers are licensed, bonded, and insured in Texas.' },
                { title: 'Satisfaction Guaranteed', desc: 'Not happy? We\'ll make it right or you don\'t pay.' }
              ].map((item) => (
                <Card key={item.title} className="border-0 shadow-lg text-center">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-[#1a3a5c] mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Financing Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <Card className="bg-gradient-to-r from-[#1a3a5c] to-[#2a5a8c] border-0">
              <CardContent className="p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="text-white">
                    <h2 className="text-2xl md:text-3xl font-bold mb-4">
                      Financing Available
                    </h2>
                    <p className="text-blue-100 mb-6">
                      Don&apos;t let budget stop you from fixing your plumbing problems. We offer 
                      financing through GreenSky with affordable monthly payments and quick approval.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-blue-100">
                        <CheckCircle size={18} className="text-[#e85a24]" />
                        Quick approval process
                      </li>
                      <li className="flex items-center gap-2 text-blue-100">
                        <CheckCircle size={18} className="text-[#e85a24]" />
                        Flexible payment terms
                      </li>
                      <li className="flex items-center gap-2 text-blue-100">
                        <CheckCircle size={18} className="text-[#e85a24]" />
                        Competitive rates
                      </li>
                    </ul>
                    <Link to="/contact">
                      <Button className="bg-[#e85a24] hover:bg-[#d14a14] text-white">
                        Ask About Financing
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </div>
                  <div className="hidden md:flex justify-center">
                    <div className="bg-white rounded-xl p-8 shadow-lg max-w-sm">
                      <p className="text-[#1a3a5c] font-bold text-xl mb-4">GreenSky Financing</p>
                      <p className="text-gray-600 mb-4">Affordable monthly payments for:</p>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Water heater replacement</li>
                        <li>• Sewer line repair</li>
                        <li>• Whole-house repiping</li>
                        <li>• Major plumbing projects</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#1a3a5c] mb-4">Service Areas</h2>
              <p className="text-gray-600">Proudly serving the Permian Basin</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {businessInfo.serviceAreas.map((area) => (
                <Link 
                  key={area}
                  to={area === 'Midland' ? '/plumbers-in-midland' : '/contact'}
                  className="px-5 py-2 bg-gray-100 border border-gray-200 rounded-full hover:border-[#1a3a5c] hover:bg-[#1a3a5c] hover:text-white transition-all text-gray-700 font-medium"
                >
                  {area}, TX
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready to Schedule Your Service?"
          subtitle="Book online or call us directly. We'll get your plumbing working right."
        />
      </main>

      <Footer />
    </>
  );
};

export default ServicesPage;
